import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, X, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '@/services/authService';

import logoWhite from '@/assets/logow.svg';
import symposiumText from '@/assets/symposiam.svg';
import bgLogin from '@/assets/bglogin.jpg';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle'); // idle | loading | success | error
  const [forgotMsg, setForgotMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await authService.login(formData);
      if (result.success) {
        navigate('/account');
      } else {
        setError(result.message || 'อีเมลหรือรหัสผ่านไม่ถูกต้อง');
      }
    } catch (err) {
      setError('เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setForgotEmail('');
    setForgotStatus('idle');
    setForgotMsg('');
    setShowForgot(true);
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotStatus('loading');
    setForgotMsg('');
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setForgotStatus('success');
        setForgotMsg(data.message || 'ส่งลิงก์รีเซ็ตรหัสผ่านแล้ว กรุณาตรวจสอบอีเมลของคุณ');
      } else {
        setForgotStatus('error');
        setForgotMsg(data.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
      }
    } catch {
      setForgotStatus('error');
      setForgotMsg('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#8B7DC3] via-[#B3FFD1] to-[#BFB4EE] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#533193] rounded-full opacity-20 -translate-x-48 -translate-y-48 transform rotate-45"></div>
      <div className="absolute top-20 right-0 w-64 h-64 bg-[#533193] rounded-full opacity-30 translate-x-32 -translate-y-12 transform rotate-12"></div>
      <div className="absolute bottom-0 left-20 w-80 h-80 bg-[#BFB4EE] rounded-full opacity-25 -translate-y-20"></div>
      <div className="absolute bottom-20 right-20 w-48 h-48 bg-[#533193] rounded-full opacity-20"></div>

      {/* Main Login Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full relative z-10"
        style={{ height: '600px' }}
      >
        <div className="flex h-full">
          {/* Left Panel - Background Image */}
          <div className="flex-1 relative overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(83, 49, 147, 0.7) 0%, rgba(191, 180, 238, 0.3) 100%), url(${bgLogin})`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#533193]/50 to-transparent" />
            
            {/* Decorative Elements on Image */}
            <div className="absolute top-8 left-8">
              <div className="w-16 h-16 border-2 border-white/30 rounded-lg transform rotate-12"></div>
            </div>
            <div className="absolute bottom-16 right-12">
              <div className="w-12 h-12 bg-white/20 rounded-full"></div>
            </div>
            <div className="absolute top-1/3 right-8">
              <div className="w-8 h-8 border border-white/40 rounded-full"></div>
            </div>
          </div>

          {/* Right Panel - Login Form */}
          <div className="flex-1 p-12 flex flex-col justify-center bg-white relative">
            {/* Logo */}
            <div className="absolute top-6 right-6">
              <div className="flex flex-col items-end">
                <img src={logoWhite} alt="SACIT" className="h-6 w-auto mb-1 filter brightness-0" />
                <img src={symposiumText} alt="Symposium" className="h-7 w-auto filter brightness-0" />
              </div>
            </div>

            {/* Login Form */}
            <div className="max-w-sm mx-auto w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h1 className="text-3xl font-didot text-[#533193] mb-8 text-center">
                  Login
                </h1>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 text-center"
                  >
                    <span className="text-red-600 text-sm">{error}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#533193] focus:ring-0 font-custom"
                      required
                    />
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2 relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#533193] focus:ring-0 font-custom"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 text-[#533193] border-gray-300 rounded focus:ring-[#533193]"
                      />
                      <span className="font-custom text-gray-600">Remember Me</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="font-custom text-[#533193] hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>

                  {/* Login Button */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-[#533193] to-[#8B7DC3] hover:from-[#533193]/90 hover:to-[#8B7DC3]/90 text-white py-3 rounded-xl font-custom-bold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'Login'}
                    </Button>
                  </motion.div>
                </form>

                {/* Divider */}
                <div className="my-8 flex items-center">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="px-4 text-sm font-custom text-gray-500">หรือ</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>

                {/* Register Link */}
                <div className="text-center">
                  <p className="font-custom text-gray-600">
                    ยังไม่มีบัญชี?{' '}
                    <Link 
                      to="/register" 
                      className="font-custom-bold text-[#533193] hover:underline"
                    >
                      สมัครสมาชิก
                    </Link>
                  </p>
                </div>

                {/* Back to Home */}
                <div className="text-center mt-6">
                  <Link 
                    to="/" 
                    className="font-custom text-sm text-gray-500 hover:text-[#533193] transition-colors"
                  >
                    ← กลับหน้าหลัก
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
      {/* ── Forgot Password Modal ── */}
      <AnimatePresence>
        {showForgot && (
          <motion.div
            key="forgot-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowForgot(false); }}
          >
            <motion.div
              key="forgot-card"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* Modal header */}
              <div className="px-8 pt-8 pb-6 relative">
                <button
                  onClick={() => setShowForgot(false)}
                  className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition"
                >
                  <X className="w-5 h-5" />
                </button>

                {forgotStatus === 'success' ? (
                  <div className="text-center py-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                      <CheckCircle2 className="w-9 h-9 text-green-500" />
                    </motion.div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">ส่งอีเมลแล้ว!</h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">{forgotMsg}</p>
                    <p className="text-xs text-gray-400 mb-6">
                      กรุณาตรวจสอบกล่องจดหมายหรือโฟลเดอร์ Spam ของคุณ<br />
                      ลิงก์จะหมดอายุใน 1 ชั่วโมง
                    </p>
                    <Button
                      onClick={() => setShowForgot(false)}
                      className="w-full bg-gradient-to-r from-[#533193] to-[#8B7DC3] text-white rounded-xl py-2.5"
                    >
                      กลับไปหน้า Login
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#533193]" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-800">ลืมรหัสผ่าน?</h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                      กรอกอีเมลที่ลงทะเบียนไว้ เราจะส่งลิงก์รีเซ็ตรหัสผ่านให้คุณ
                    </p>

                    {forgotStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-600"
                      >
                        {forgotMsg}
                      </motion.div>
                    )}

                    <form onSubmit={handleSendReset} className="space-y-4">
                      <Input
                        type="email"
                        placeholder="อีเมลของคุณ"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#533193] focus:ring-0"
                        required
                        disabled={forgotStatus === 'loading'}
                        autoFocus
                      />
                      <Button
                        type="submit"
                        disabled={forgotStatus === 'loading' || !forgotEmail}
                        className="w-full bg-gradient-to-r from-[#533193] to-[#8B7DC3] hover:opacity-90 text-white py-3 rounded-xl font-bold text-base shadow-md disabled:opacity-50"
                      >
                        {forgotStatus === 'loading'
                          ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> กำลังส่ง...</span>
                          : 'ส่งลิงก์รีเซ็ตรหัสผ่าน'
                        }
                      </Button>
                      <button
                        type="button"
                        onClick={() => setShowForgot(false)}
                        className="w-full text-center text-sm text-gray-500 hover:text-[#533193] transition py-1"
                      >
                        ยกเลิก
                      </button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;